import dayjs from "dayjs";
import { ClockAlert, CalendarArrowUp, History } from "lucide-react";

/**
 * =========================
 * Bin / Status helpers
 * =========================
 */

export interface BinBase {
  key: "overdue" | "current" | "history";
  name: string;
  icon: any;
}

export const imageUrl = (image: string, acceptSlashes = false) => {
  const base = (import.meta.env.VITE_APP_URL || "").replace(/\/$/, "");

  const encoded = acceptSlashes ? encodeURIComponent(image).replace(/%2F/g, "/") : encodeURIComponent(image);

  return `${base}/image/${encoded}`;
};

export interface BinDefinition extends BinBase {
  rule: (event: BobEvent) => boolean;
}

export interface Bin extends BinBase {
  events: BobEvent[];
}

/**
 * =========================
 * Message / Testing helpers
 * =========================
 */

export interface TestData {
  title: string;
  contents: string[];
}

export type EditedTestData = TestData & {
  comment: string;
  failed: boolean | null;
};

export type BobEventMessageBase = {
  id: number;
  runningHours: {
    resource: number;
    id?: number;
    runningHours: number;
  } | null;
  format: "text" | "testing_json";
  message: string | EditedTestData[];
  attachments: any[];
};

export type BobEventMessageSetter = BobEventMessageBase & {
  event?: number;
};

export type BobEventMessage = BobEventMessageBase & {
  createdAt: string;
  createdBy: {
    id: number;
    name: string;
    firstName: string;
  };
};

/**
 * =========================
 * Event core types
 * =========================
 */

export type DamageType = "technical_defect" | "casco_own_fault" | "liability_third_party_fault" | "liability_own_fault" | "partial_casco";

export interface DamageRegulationData {
  incidentDate?: string;
  damageType?: DamageType;
  appraisalCost?: number;
  invoiceCost?: number;
  deductible?: number;
  reimbursedAmount?: number;
}

export interface BobEventBase {
  id: number;
  scheduledAt: string | null;
  scheduledDate?: string | null;
  completedAt: string | null;
  dueAt: string | null;
  damageRegulationData: DamageRegulationData | null;
  dueAtAmount: number | null;
  copyRuleDurationUnit: "day" | "week" | "month" | "year" | "amount" | null;
  copyRuleDurationValue: number | null;
  category: "external" | "meterReading" | "testing" | "maintenance" | "damage";
  maintenanceType: "uvv" | "tuev" | "inspection" | "sp" | "tacho" | "gas" | "other" | null;
  title: string;
  priority: "low" | "normal" | "high";
  assignedTo: "none" | "site" | "service";
}

export type BobEventSetter = BobEventBase & {
  resource?: number;
  messages: BobEventMessageSetter[];
};

export type BobEvent = BobEventBase & {
  categorySettings: {
    showCompleted: boolean;
    title: string;
  };
  createdAt: string;
  messageCount: number;
  autoCreationKey: string | null;
};

/**
 * =========================
 * Bin logic (shared list + grid)
 * =========================
 */

export const getBinDefinitons = (role: "site" | "service"): BinDefinition[] => [
  {
    key: "overdue",
    name: "Überfällig",
    icon: ClockAlert,
    rule: (event) => !event.completedAt && event.assignedTo === role && !!event.dueAt && dayjs(event.dueAt, "YYYY-MM-DD").isBefore(dayjs(), "day"),
  },
  {
    key: "current",
    name: "Offen",
    icon: CalendarArrowUp,
    rule: (event) => !event.completedAt,
  },
  {
    key: "history",
    name: "Historie",
    icon: History,
    rule: (event) => !!event.completedAt,
  },
];

const parseDate = (d: string | null, isDueAt = false): number =>
  d ? (isDueAt ? dayjs(d, "YYYY-MM-DD").valueOf() : dayjs(d).valueOf()) : Number.NEGATIVE_INFINITY;

export const eventTimeMs = (e: BobEvent, dateOnlyScheduling = false): number => {
  if (e.completedAt) return parseDate(e.completedAt);
  if (e.scheduledDate) return parseDate(e.scheduledDate, true);
  if (e.scheduledAt) return dateOnlyScheduling ? dayjs.utc(e.scheduledAt).valueOf() : parseDate(e.scheduledAt);
  if (e.dueAt) return parseDate(e.dueAt, true);
  return parseDate(e.createdAt);
};

export const generateBins = (events: BobEvent[], definitions: BinDefinition[], dateOnlyScheduling = false): Bin[] => {
  const bins: Bin[] = definitions.map(({ key, name, icon }) => ({
    key,
    name,
    icon,
    events: [],
  }));

  const priorityRank: Record<BobEvent["priority"], number> = {
    high: 3,
    normal: 2,
    low: 1,
  };

  const compareByFallbackTime = (a: BobEvent, b: BobEvent) => {
    // If a is not completed, sort by priority first
    if (!a.completedAt) {
      const priorityDiff = priorityRank[b.priority] - priorityRank[a.priority];

      if (priorityDiff !== 0) {
        return priorityDiff;
      }
    }

    // Fallback to time-based sorting
    return a.completedAt ? eventTimeMs(b, dateOnlyScheduling) - eventTimeMs(a, dateOnlyScheduling)
      : eventTimeMs(a, dateOnlyScheduling) - eventTimeMs(b, dateOnlyScheduling);
  };

  for (const ev of events) {
    if (ev.completedAt && !ev.categorySettings.showCompleted) continue;
    const idx = definitions.findIndex((d) => d.rule(ev));
    if (idx !== -1) bins[idx].events.push(ev);
  }

  for (const bin of bins) {
    bin.events.sort(compareByFallbackTime);
  }

  return bins.filter((b) => b.events.length > 0);
};

/**
 * =========================
 * Subtitle (shared table + sidebar)
 * =========================
 */

export const getSubtitle = (event: BobEvent, dateOnlyScheduling = false): string => {
  if (event.completedAt) {
    return `Erledigt am ${dayjs(event.completedAt).format("ddd, DD.MM.YYYY")}`;
  }

  let duePart = "";

  if (event.dueAtAmount) {
    duePart = `Fällig bei ${event.dueAtAmount} h`;
    if (event.dueAt) {
      duePart += ` (ca. ${dayjs(event.dueAt, "YYYY-MM-DD").fromNow()})`;
    }
  } else if (event.dueAt) {
    duePart = `Fällig am ${dayjs(event.dueAt, "YYYY-MM-DD").format("ddd, DD.MM.YYYY")}`;
  }

  if (event.scheduledDate || event.scheduledAt) {
    const date = event.scheduledDate ? dayjs(event.scheduledDate)
      : dateOnlyScheduling ? dayjs.utc(event.scheduledAt).local() : dayjs(event.scheduledAt);
    return `Geplant für ${date.format("ddd, DD.MM.YYYY")}${event.scheduledDate ? " (ohne Uhrzeit)" : ""}` + (duePart ? ` – ${duePart}` : "");
  }

  if (duePart) return duePart;

  return `Erstellt am ${dayjs(event.createdAt).format("ddd, DD.MM.YYYY")}`;
};
